// Weekly settle for the Services and Social Media ventures. Both run alongside the
// business at SettleWeek, so their income lands in the week's cashflow — and from
// there flows into the Payday harvest like any other profit.

import type { GameState } from '../state/types.js';
import { applyTxn } from '../ledger/append.js';
import { nextFloat } from '../rng/prng.js';
import { serviceWeeklyResult } from './services.js';
import { socialGrowth, brandDeals } from './social.js';
import {
  practiceGain,
  tournamentPrize,
  streamIncome,
  PRACTICE_ENERGY,
  TOURNAMENT_ENTRY,
} from './gaming.js';
import { carryCost } from './trade.js';
import { upkeepCost, rentIncome } from './property.js';
import { generateDeals, closeChance, CLOSE_REP_GAIN } from './broker.js';

/** Services: work the planned hours (capped by energy + demand), collect the rate,
 *  and spend the energy. High margin, hard-capped by hours. */
export function settleServices(state: GameState): GameState {
  if (!state.services.active) return state;
  const r = serviceWeeklyResult({
    rate: state.services.hourlyRate,
    plannedHours: state.services.plannedHours,
    energy: state.profile.happiness,
  });
  if (r.hoursWorked <= 0) return state;

  let s = applyTxn(state, {
    type: 'service_income',
    amount: r.revenue,
    category: 'active',
    note: `Services — ${r.hoursWorked}h @ B$${state.services.hourlyRate}/h`,
  });
  s = { ...s, profile: { ...s.profile, happiness: Math.max(0, s.profile.happiness - r.energyCost) } };
  return s;
}

/** Social: grow the audience (posting helps, silence fades it), then pay out any
 *  brand deals unlocked at the new follower count. The audience is the asset. */
export function settleSocial(state: GameState): GameState {
  if (!state.social.active) return state;
  const followers = socialGrowth(state.social.followers, state.social.postedThisWeek);
  let s: GameState = { ...state, social: { ...state.social, followers, postedThisWeek: false } };

  const { weeklyIncome } = brandDeals(followers);
  if (weeklyIncome > 0) {
    s = applyTxn(s, {
      type: 'brand_deal',
      amount: weeklyIncome,
      category: 'passive',
      note: `Brand deals — ${followers.toLocaleString('en-US')} followers`,
    });
  }
  return s;
}

/** Gaming: resolve this week's move. Practice raises skill (costs energy); compete
 *  pays a skill-scaled prize (a deterministic luck draw from the seeded PRNG); going
 *  pro pays passive streaming income every week. Then reset the weekly choice. */
export function settleGaming(state: GameState): GameState {
  if (!state.gaming.active) return state;
  let s = state;
  const action = state.gaming.weekAction;

  if (action === 'practice' && s.profile.happiness >= PRACTICE_ENERGY) {
    const gain = practiceGain(s.gaming.skill);
    s = {
      ...s,
      gaming: { ...s.gaming, skill: Math.min(100, s.gaming.skill + gain) },
      profile: { ...s.profile, happiness: Math.max(0, s.profile.happiness - PRACTICE_ENERGY) },
    };
  } else if (action === 'compete' && s.wallet.cash >= TOURNAMENT_ENTRY) {
    const { value: luck, nextCursor } = nextFloat(s.rngSeed, s.rngCursor);
    s = { ...s, rngCursor: nextCursor };
    const prize = tournamentPrize(s.gaming.skill, luck);
    // One net txn so the cashflow reads cleanly: prize minus the entry you paid.
    s = applyTxn(s, {
      type: 'gaming_prize',
      amount: prize - TOURNAMENT_ENTRY,
      category: 'active',
      note: `Tournament — entry B$${TOURNAMENT_ENTRY}, prize B$${prize}`,
    });
  }

  // Going pro: mastery pays passively, whatever you chose this week.
  const stream = streamIncome(s.gaming.skill);
  if (stream > 0) {
    s = applyTxn(s, {
      type: 'gaming_stream',
      amount: stream,
      category: 'passive',
      note: `Streaming income — skill ${s.gaming.skill}`,
    });
  }

  // Spend this week's move.
  return { ...s, gaming: { ...s.gaming, weekAction: 'rest' } };
}

/** Trade & resell: holding isn't free. Every item in the inventory costs a weekly
 *  carrying cost — the longer you sit on stock, the more it eats your profit. */
export function settleTrade(state: GameState): GameState {
  if (!state.trade.active || state.trade.inventory.length === 0) return state;
  let s = state;
  for (const item of state.trade.inventory) {
    const cost = carryCost(item.fairValue);
    s = applyTxn(s, {
      type: 'trade_carry',
      amount: -cost,
      category: 'expense',
      note: `Carrying ${item.name}`,
    });
  }
  return s;
}

/** Property: ownership is never neutral. An EMPTY property costs upkeep every
 *  week; a RENTED one pays rent instead (the tenant covers the upkeep). */
export function settleProperty(state: GameState): GameState {
  if (!state.property.active || state.property.portfolio.length === 0) return state;
  let s = state;
  for (const prop of state.property.portfolio) {
    if (prop.rented) {
      s = applyTxn(s, {
        type: 'rent_income',
        amount: rentIncome(prop),
        category: 'passive',
        note: `Rent — ${prop.name}`,
      });
    } else {
      s = applyTxn(s, {
        type: 'property_upkeep',
        amount: -upkeepCost(prop),
        category: 'expense',
        note: `Upkeep — ${prop.name} (empty)`,
      });
    }
  }
  return s;
}

/** Broker: resolve the deal worked this week (if any) against the close chance
 *  shown to the player. A close pays the commission and grows reputation (the
 *  compounding asset); a miss costs nothing further — the energy already went.
 *  The luck draw advances the shared cursor ONLY when a deal resolves. */
export function settleBroker(state: GameState): GameState {
  if (!state.broker.active) return state;
  const { workingSlot, workingWeek } = state.broker;
  const week = state.clock.week;
  if (workingSlot === null || workingWeek !== week) {
    // Nothing worked this week — just make sure the slot marker is cleared.
    return workingSlot === null ? state : { ...state, broker: { ...state.broker, workingSlot: null } };
  }

  const deal = generateDeals(state.rngSeed, week).find((d) => d.slot === workingSlot);
  let s: GameState = { ...state, broker: { ...state.broker, workingSlot: null } };
  if (!deal) return s;

  const { value: luck, nextCursor } = nextFloat(s.rngSeed, s.rngCursor);
  s = { ...s, rngCursor: nextCursor };
  const chance = closeChance(s.profile.reputation, s.broker.dealsClosed);

  if (luck < chance) {
    s = applyTxn(s, {
      type: 'broker_commission',
      amount: deal.commission,
      category: 'active',
      note: `Closed: ${deal.need} ↔ ${deal.supply} (B$${deal.dealSize} deal)`,
    });
    s = {
      ...s,
      broker: { ...s.broker, dealsClosed: s.broker.dealsClosed + 1 },
      profile: { ...s.profile, reputation: Math.min(100, s.profile.reputation + CLOSE_REP_GAIN) },
    };
  }
  return s;
}
